import * as React from 'react';
import type {
	FieldValues,
	UseControllerProps,
	ControllerRenderProps,
	Path,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

import { isErrorType, type CellError } from 'af-components/RHFTableCellComponents/error.types';

export type RenderMethodParams<T extends FieldValues> = {
	field: ControllerRenderProps<T, Path<T>>;
	onFieldChange?: (event: React.ChangeEvent) => void;
	error?: Nullable<CellError>;
};

export type RHFFieldWrapperProps<T extends FieldValues> = UseControllerProps<T> & {
	onChange?: (name: string) => void;
	render: (params: RenderMethodParams<T>) => React.ReactElement;
};

/**
 * Field Wrapper around components
 * Offers controlled component by react hook form, tracks and updates RHF’s internal value store
 */
function RHFPlainFieldWrapper<T extends FieldValues>(props: RHFFieldWrapperProps<T>) {
	const { name, control, defaultValue, rules, onChange, render, shouldUnregister = false } = props;

	const { field, fieldState } = useController<T>({
		name,
		control,
		defaultValue,
		rules,
		shouldUnregister,
	});

	const onFieldChange = React.useCallback((event) => {
		field.onChange(event);
		onChange?.(name);
	}, [field, name, onChange]);

	const error: Nullable<CellError> = React.useMemo(() => {
		if (fieldState.error?.message) {
			return {
				message: fieldState.error.message,
				type: isErrorType(fieldState.error.type)
					? fieldState.error.type
					: 'other',
			};
		} else {
			return null;
		}
	}, [fieldState]);

	return render({
		field,
		error,
		onFieldChange,
	});
}

export default React.memo(RHFPlainFieldWrapper) as typeof RHFPlainFieldWrapper;
